import convert from "convert-units";
import cloneDeep from "lodash/cloneDeep";
import find from "lodash/find";
import get from "lodash/get";
import lround from "lodash/round";
import moment from "moment";
import React from "react";
import { withTranslation } from "react-i18next";
import submit from "submit-form";
// Constants
import {
  DATE_FORMATS,
  defaultFirebaseProjectId,
  PRIMARIESWITHOLYMPIC,
  S3,
} from "../../../constants";
import { AthleteLinks } from "../../../parts/athlete-links";
import Avatar from "../../../parts/avatar";
import DatePicker from "../../../parts/DatePicker";
import { AthleteSelect } from "../../../parts/inputs/athlete-select-new";
import ToggleLinksInput from "../../../parts/inputs/toggle-links-input";
import InsertUndoToast from "../../../parts/InsertUndoToast";
import SvgIcon from "../../../parts/SvgIcon";
import VideoRecordComponent from "../../../parts/VideoRecordComponent";
import { RillContext } from "../../../rill/RillReact/share/base";
import addMetadata from "../../../utils/add-metadata";
import { groupExercisesByComplex } from "../../../utils/complex";
import {
  checkOtherExercise,
  validateSet,
} from "../../../utils/exercise-validations";
import getAvatarUrl from "../../../utils/get-avatar-url";
import getSessionOrder from "../../../utils/get-session-order";
import { getHighestOrder } from "../../../utils/prefix-calculator";
import { pickWorkoutNote } from "../../../utils/section-types";
import QuickMetrics from "../../dashboard/calendar/components/QuickMetrics";
import SingleDayCycleBar from "../../dashboard/calendar/components/SingleDayCycleBar";
import TopBarSingleDay from "../../dashboard/calendar/components/TopBarSingleDay";
import DaylyWorkoutHeader from "../../dashboard/calendar/DaylyWorkoutHeader";
import ExerciseFrame from "../../dashboard/calendar/ExerciseFrame";
import PhotoModal from "./progress-photos/PhotoModal";
import Picture from "./progress-photos/Picture";

const { DATE_YEAR_FORMAT, DATE_DAY_FORMAT } = DATE_FORMATS;

class View extends React.Component {
  constructor(props, context) {
    super(props);
    const { activeUser, athlete } = context.locals;
    this.user = activeUser;
    this.activeUser = athlete || activeUser;
    this.state = {
      coachNotes: "",
      openQuickEdit: false,
      // "+ Instructions" in the day header opens a new note in edit mode.
      creatingWorkoutNote: false,
      openQuickEditCoachNotes: false,
      exercises: [],
      photoModal: {
        open: false,
        type: "",
        pictureImg: "",
      },
    };
    this.lastDate = context.locals.query?.date;
    this.loadInputs = [];
    this.intensityInputs = [];
    this.handleDuplicateExercise = this.handleDuplicateExercise.bind(this);
    this.openQuickEditMode = this.openQuickEditMode.bind(this);
    this.handleQuickEditChange = this.handleQuickEditChange.bind(this);
    this.handleAddSetLocally = this.handleAddSetLocally.bind(this);
    this.saveQuickEdit = this.saveQuickEdit.bind(this);
    this.closeQuickEditMode = this.closeQuickEditMode.bind(this);
    this.toggleModal = this.toggleModal.bind(this);
  }

  componentDidUpdate(prevProps) {
    const { editedExercise, setIndex } = this.props;

    // Day arrows reuse this instance: drop an unsaved "+ Instructions" draft
    // so create mode doesn't carry over to the next day.
    const currentDate = this.context.locals.query?.date;
    if (this.lastDate !== currentDate) {
      if (this.lastDate !== undefined && this.state.creatingWorkoutNote) {
        this.setState({ creatingWorkoutNote: false });
      }
      this.lastDate = currentDate;
    }

    // Early return if no changes to sync
    if (
      !editedExercise ||
      (editedExercise === prevProps.editedExercise &&
        setIndex === prevProps.setIndex)
    ) {
      return;
    }

    const updatedExercise = this.context.locals.activeExercises.find(
      (ex) => ex._id === editedExercise
    );
    const stateExercise = this.state.exercises.find(
      (ex) => ex._id === editedExercise
    );

    if (!updatedExercise) return;

    // We have a stateExercise - sync set changes
    if (
      stateExercise &&
      stateExercise.sets?.length !== updatedExercise.sets?.length
    ) {
      this.setState((prevState) => {
        const copyExercises = [...prevState.exercises];
        const exerciseIndex = copyExercises.findIndex(
          (elem) => elem._id === editedExercise
        );

        if (exerciseIndex === -1) return prevState;

        const stateSets = copyExercises[exerciseIndex].sets;
        const updatedSets = updatedExercise.sets;

        // Handle set addition - preserve existing changes and add new set at correct position
        if (
          updatedSets.length > stateSets.length &&
          setIndex !== undefined &&
          setIndex >= 0
        ) {
          const newSet = updatedSets[setIndex + 1];
          if (newSet) {
            const newSets = [...stateSets];
            newSets.splice(setIndex + 1, 0, newSet);
            copyExercises[exerciseIndex].sets = newSets;
          }
        }
        // Handle set deletion - accept potential loss of unsaved changes
        else if (updatedSets.length < stateSets.length) {
          copyExercises[exerciseIndex].sets = [...updatedSets];
        }

        return { exercises: copyExercises };
      });
    }
    // No stateExercise but we need to track changes - add entire exercise
    else if (!stateExercise && setIndex !== undefined && setIndex >= 0) {
      this.setState((prevState) => ({
        exercises: [...prevState.exercises, { ...updatedExercise }],
      }));
    }
  }

  toggleModal(pictureImg = "", type = "") {
    this.setState({
      photoModal: {
        open: !this.state.photoModal.open,
        type,
        pictureImg: S3.toCDN(pictureImg),
      },
    });
  }

  handleDuplicateExercise(body, proxyAthlete) {
    submit(
      `/actions/exercise/duplicate-from-single-day?proxyAthlete=${proxyAthlete}`,
      {
        method: "POST",
        body,
      }
    );
  }

  openQuickEditMode(e) {
    e.preventDefault();
    this.setState((prevState) =>
      Object.assign({}, prevState, { openQuickEdit: true })
    );
  }

  closeQuickEditMode(e) {
    e?.preventDefault();
    document.getElementById("quick-edit").reset();
    this.setState((prevState) =>
      Object.assign({}, prevState, { openQuickEdit: false })
    );
  }

  handleQuickEditChange({ target: { name, value } }, exId, setId) {
    const { locals } = this.context;
    const exercises = cloneDeep(this.state.exercises);
    const exerciseIndex = exercises.findIndex((ex) => ex._id === exId);
    const exercise =
      exercises[exerciseIndex] ||
      cloneDeep(get(locals, "activeExercises", [])).find(
        (ex) => ex._id === exId
      ) ||
      cloneDeep(get(locals, "editExercises", [])).find(
        (ex) => ex._id === exId
      ) ||
      {};

    const set = get(exercise, ["sets", setId], false);

    if (["load", "percentRepMax", "loadDrop"].includes(name)) {
      if (String(value).includes(",")) {
        value = String(value).replace(",", ".");
      }
    }

    if (set && set[name] !== value && name !== "exerciseType") {
      set[name] = value;
      const primary = get(exercise, "primary");
      const repMax =
        exercise.type !== "secondary"
          ? get(
              exercise,
              `repMaximums.${primary}`,
              get(this.activeUser, `repMaximums.${primary}`, 0)
            )
          : exercise.secondaryRepMaximum;

      // ensure load and percentRepMax are in sync
      if (name === "load") {
        if (locals.activeUser.units === "imperial") {
          set.load = lround(convert(value).from("lb").to("kg"), 2);
        }

        if (exercise.type !== "assistant")
          set.percentRepMax = lround((set.load / repMax) * 100, 1);
      }

      if (name === "percentRepMax")
        set.load = lround((repMax * set.percentRepMax) / 100, 2);

      const loadInput = this.loadInputs[`${exId}_${setId}`];
      if (loadInput) {
        if (name === "percentRepMax") {
          let loadToShow = set.load;
          if (locals.activeUser.units === "imperial") {
            loadToShow = lround(convert(loadToShow).from("kg").to("lb"), 2);
          }
          loadInput.value = loadToShow;
        }
        if (!this.intensityInputs[`${exId}_${setId}`]) {
          this.intensityInputs[`${exId}_${setId}`] = { value: 0 };
        }
        this.intensityInputs[`${exId}_${setId}`].value = set.percentRepMax ?? 0;
      }

      const validatedSet = validateSet({
        exercise,
        set,
        locals,
        field: name,
      });
      if (validatedSet) {
        exercise.sets[setId] = validatedSet;

        // Handle complex exercises - update other exercises in the same complex
        if (
          exercise.complex &&
          exercise.complexInstance &&
          ["sets", "rpe", "load", "percentRepMax"].includes(name)
        ) {
          // Find all exercises in the same complex from all available sources
          const allAvailableExercises = [
            ...exercises,
            ...cloneDeep(get(this.context.locals, "activeExercises", [])),
            ...cloneDeep(get(this.context.locals, "editExercises", [])),
          ];

          // Remove duplicates based on _id
          const uniqueExercises = allAvailableExercises.reduce((acc, ex) => {
            if (
              !acc.find(
                (existing) => existing._id.toString() === ex._id.toString()
              )
            ) {
              acc.push(ex);
            }
            return acc;
          }, []);

          // Find all exercises in the same complex
          const complexExercises = uniqueExercises.filter(
            (ex) =>
              ex.complex === exercise.complex &&
              ex.complexInstance === exercise.complexInstance &&
              ex._id.toString() !== exId.toString() // Exclude the current exercise
          );

          // Update each exercise in the complex
          complexExercises.forEach((complexEx) => {
            let complexExIndex = exercises.findIndex(
              (ex) => ex._id.toString() === complexEx._id.toString()
            );

            // If the complex exercise doesn't exist in exercises array, add it
            if (complexExIndex === -1) {
              exercises.push(cloneDeep(complexEx));
              complexExIndex = exercises.length - 1;
            }

            if (
              complexExIndex !== -1 &&
              exercises[complexExIndex].sets &&
              exercises[complexExIndex].sets[setId]
            ) {
              // Update the corresponding set
              const complexSet = exercises[complexExIndex].sets[setId];
              complexSet[name] = value;

              // Validate the complex exercise set
              const validatedComplexSet = validateSet({
                exercise: exercises[complexExIndex],
                set: complexSet,
                locals,
                field: name,
              });

              if (validatedComplexSet) {
                exercises[complexExIndex].sets[setId] = validatedComplexSet;
              }
            }
          });
        }

        this.setExersicesStates(exercise, exercises, exerciseIndex);
      } else {
        // revert to original value
        const originalSet = cloneDeep(get(locals, "activeExercises", [])).find(
          (ex) => ex._id === exId
        )?.sets?.[setId];
        document.getElementById(`${exId}_${setId}_${name}`).value =
          originalSet?.[name] ?? "";
      }
    } else if (name === "notes") {
      exercise.notes = value === "" ? " " : value;

      this.setExersicesStates(exercise, exercises, exerciseIndex);
    } else if (name === "repsPerSide" || name === "tempo") {
      exercise[name] = value;

      this.setExersicesStates(exercise, exercises, exerciseIndex);
    } else if (name === "exerciseType") {
      const { movement, repMaximum, newPrimary } = value;
      const sets = get(exercise, "sets", false);

      addMetadata(
        exercise,
        movement,
        newPrimary,
        locals.activeUserToRequest.primaryExercises,
        locals.activeUserToRequest.exerciseLibrary
      );

      // if the movement chosen is a primary, change to a primary
      if (PRIMARIESWITHOLYMPIC.includes(movement)) {
        // setting new values
        exercise.type = "primary";
        exercise.primary = movement;
        // resetting secondary and secondary RM
        exercise.secondary = "null";
        exercise.secondaryRepMaximum = 0;
        exercise.parameters = "null";
        // updating the values of each set in the exercise
        sets.map((currentSet, idx) => {
          if (
            currentSet.load &&
            currentSet.percentRepMax &&
            currentSet.load >= 0 &&
            currentSet.percentRepMax >= 0
          ) {
            currentSet.load = lround(
              (repMaximum * currentSet.percentRepMax) / 100,
              2
            );
            currentSet.percentRepMax = lround(
              (currentSet.load / repMaximum) * 100,
              1
            );
          }

          // updating the load shown
          const loadInput = this.loadInputs[`${exId}_${idx}`];
          if (loadInput) {
            let loadToShow = currentSet.load;
            const { units } = this.context.locals.activeUser;

            if (units === "imperial") {
              loadToShow = lround(convert(loadToShow).from("kg").to("lb"), 2);
            }
            loadInput.value = loadToShow;
          }
          // Making sure no values are invalid after any changes
          if (!currentSet.load || currentSet.load === null) currentSet.load = 0;
          if (!currentSet.percentRepMax || currentSet.percentRepMax === null)
            currentSet.percentRepMax = 0;
          if (!currentSet.sets) currentSet.sets = 1;
          if (!currentSet.reps) currentSet.reps = 1;
          if (!currentSet.load && !currentSet.rpe) currentSet.rpe = 5;
        });
      }
      // isAssistant = true then it is assistant
      else if (newPrimary === "assistant") {
        exercise.type = "assistant";
        exercise.primary = "assistant";
        exercise.secondary = movement;
        exercise.secondaryRepMaximum = 0;
        if (value.parameters) exercise.parameters = value.parameters;
      }
      // else its a secondary
      else {
        // change type from primary/assistant to secondary and set new values
        exercise.type = "secondary";
        exercise.primary = newPrimary;
        exercise.secondary = movement;
        exercise.secondaryRepMaximum = repMaximum;
        exercise.parameters = "null";
        // updating the values of each set in the exercise
        sets.map((currentSet, idx) => {
          if (
            currentSet.load &&
            currentSet.percentRepMax &&
            currentSet.load >= 0 &&
            currentSet.percentRepMax >= 0
          ) {
            currentSet.load = lround(
              (repMaximum * currentSet.percentRepMax) / 100,
              2
            );
            currentSet.percentRepMax = lround(
              (currentSet.load / repMaximum) * 100,
              1
            );
          }

          // updating the load shown
          const loadInput = this.loadInputs[`${exId}_${idx}`];
          if (loadInput) {
            let loadToShow = currentSet.load;
            const { units } = this.context.locals.activeUser;

            if (units === "imperial") {
              loadToShow = lround(convert(loadToShow).from("kg").to("lb"), 2);
            }
            loadInput.value = loadToShow;
          }
          // Making sure no values are invalid after any changes
          if (!currentSet.load || currentSet.load === null) currentSet.load = 0;
          if (!currentSet.percentRepMax || currentSet.percentRepMax === null)
            currentSet.percentRepMax = 0;
          if (!currentSet.sets) currentSet.sets = 1;
          if (!currentSet.reps) currentSet.reps = 1;
          if (!currentSet.load && !currentSet.rpe) currentSet.rpe = 5;
        });
      }

      this.setExersicesStates(exercise, exercises, exerciseIndex, true);
    }
  }

  // Adds a newly-entered set straight into the quick-edit local state and
  // auto-saves, instead of POSTing to /exercise/quick-edit/sets/add directly.
  // The direct POST used to race the whole-form saveQuickEdit PUT: both write
  // the full exercise document, and whichever lands last in Mongo wins,
  // silently dropping the other's changes (lost the new set or the edits made
  // to other sets in the same save). Routing through the same
  // setExersicesStates/saveQuickEdit path as every other quick-edit change
  // guarantees a single writer.
  handleAddSetLocally(
    exId,
    setIndex,
    newSet,
    complexExerciseIds = [],
    repsTwo,
    repsThree
  ) {
    const { locals } = this.context;
    const exercises = cloneDeep(this.state.exercises);
    const exerciseIndex = exercises.findIndex((ex) => ex._id === exId);
    const exercise =
      exercises[exerciseIndex] ||
      cloneDeep(get(locals, "activeExercises", [])).find(
        (ex) => ex._id === exId
      ) ||
      cloneDeep(get(locals, "editExercises", [])).find(
        (ex) => ex._id === exId
      ) ||
      {};

    exercise.sets.splice(parseInt(setIndex, 10) + 1, 0, newSet);

    complexExerciseIds.forEach((complexExId) => {
      const complexIndex = exercises.findIndex((ex) => ex._id === complexExId);
      let complexExercise = exercises[complexIndex];

      if (!complexExercise) {
        complexExercise =
          cloneDeep(get(locals, "activeExercises", [])).find(
            (ex) => ex._id === complexExId
          ) ||
          cloneDeep(get(locals, "editExercises", [])).find(
            (ex) => ex._id === complexExId
          );
        if (complexExercise) {
          exercises.push(complexExercise);
        }
      }

      if (complexExercise) {
        const complexSet = { ...newSet };
        if (complexExercise.indexInComplex === 1)
          complexSet.reps = repsTwo || newSet.reps;
        if (complexExercise.indexInComplex === 2)
          complexSet.reps = repsThree || newSet.reps;
        complexExercise.sets.splice(parseInt(setIndex, 10) + 1, 0, complexSet);
      }
    });

    this.setExersicesStates(exercise, exercises, exerciseIndex, true);
  }

  setExersicesStates(
    exercise,
    exercises,
    exerciseIndex,
    saveAfterChange = false
  ) {
    // if exercise already exists in state
    if (exerciseIndex > -1) exercises[exerciseIndex] = exercise;
    // otherwise push to exercises
    if (exerciseIndex < 0) exercises.push(exercise);

    this.setState(
      (prevState) => Object.assign({}, prevState, { exercises }),
      saveAfterChange
        ? () =>
            this.saveQuickEdit(
              { preventDefault: () => {} },
              this.context.locals.query.athlete,
              false
            )
        : undefined
    );
  }

  saveQuickEdit(e, proxyAthlete, close = true) {
    e.preventDefault();

    if (
      this.state.exercises === undefined ||
      this.state.exercises.length === 0
    ) {
      this.closeQuickEditMode();
      this.setState({ exercises: [] });
      submit("/actions/exercise/quick-edit/cancel");
    } else if (checkOtherExercise(this.state.exercises)) {
      const exercises = this.state.exercises.map((ex) => {
        ex.date = ex.originalDate;

        if (ex.secondary) {
          const libraryCoach = get(
            this.context.locals.activeUser,
            `exerciseLibrary.${ex.primary}`,
            []
          );

          const existsCoach = find(libraryCoach, {
            movement: ex.secondary,
          });

          if (existsCoach) {
            existsCoach.primary = ex.primary;
            ex.movementLibrary = existsCoach;
          }
        }
        return ex;
      });
      //Fixes CH-1490
      submit(
        `/actions/exercise/quick-edit?proxyAthlete=${proxyAthlete}&reqSource=calendar`,
        {
          method: "POST",
          body: { exercises },
        }
      );
      if (close) {
        this.setState({ exercises: [] });
        this.closeQuickEditMode();
      }
    }
  }

  openQuickEditCoachNotes() {
    const workoutCoachNote = this.context.locals.workoutCoachNote;
    this.setState(
      {
        openQuickEditCoachNotes: true,
        coachNotes: workoutCoachNote?.note?.note
          ? workoutCoachNote.note.note
          : "",
      },
      () => {
        document.getElementById("coach-notes-textarea")?.focus();
      }
    );
  }

  closeQuickEditCoachNotes() {
    this.setState({ openQuickEditCoachNotes: false });
  }

  handleCoachNoteChange(value) {
    this.setState({ coachNotes: value });
  }

  saveQuickEditCoachNotes() {
    const { originalQueryDate, query, activeUser, activeUserToRequest } =
      this.context.locals;

    submit("/actions/exercise/quick-edit-coach-note", {
      method: "POST",
      body: {
        note: this.state.coachNotes,
        date: originalQueryDate,
        user: query.athlete,
        coach: query.organizationCoach || activeUser._id,
        avatar:
          !!activeUser.avatar && !!activeUser.avatar.file
            ? activeUser.avatar.file
            : null,
        firebaseProjectId: activeUserToRequest.firebaseProjectId
          ? activeUserToRequest.firebaseProjectId
          : defaultFirebaseProjectId,
      },
    });

    if (this.context.locals.workoutCoachNote?.note?.note) {
      this.context.locals.workoutCoachNote.note.note = this.state.coachNotes;
    } else {
      this.context.locals.workoutCoachNote = {
        note: { note: this.state.coachNotes },
      };
    }

    this.closeQuickEditCoachNotes();
  }

  renderBodyPhotos(activeDay = null) {
    const {
      locals: { dailyStats, query },
    } = this.context;
    const existPhotos =
      dailyStats.find(
        (stat) =>
          stat.bodyPhotos &&
          activeDay.format("YYYYMMDD") === String(stat.utcDate)
      ) || false;

    if (!existPhotos) return null;
    const { athlete } = query;
    const urlStart = athlete ? "/athlete-portal/metrics" : "/dashboard/metrics";
    const urlEnd = athlete ? `&athlete=${athlete}` : "";
    const date = activeDay.format(DATE_DAY_FORMAT);
    return (
      <div className="progress-photo-wrapper">
        <span>{this.props.t("calendarDay.progressPhotos")}</span>
        <div className="photos-container">
          {["front", "back", "side"].map((photoType, idx) => {
            const pictureSrc = existPhotos.bodyPhotos[photoType] || null;
            return (
              <Picture
                key={idx}
                src={pictureSrc ? pictureSrc.s3Path : pictureSrc}
                toggleModal={this.toggleModal}
                className="body-photo"
                photoType={photoType}
                loaded
              />
            );
          })}
        </div>
        <a
          href={`${urlStart}/progress-photos?view=day&date=${date}${urlEnd}`}
          data-button-type="text"
          role="button"
        >
          {this.props.t("calendarDay.seeAll")}
        </a>
      </div>
    );
  }

  renderNotes({
    notes,
    coachNotes,
    athleteInfo,
    activeUserToRequest,
    coachOrganizationInformation,
  }) {
    return (
      <div className="notes-container">
        <div className="coach-notesx">
          <label>{this.props.t("calendarDay.notes")}</label>
          {!this.state.openQuickEditCoachNotes ? (
            <div
              className="edit-mode"
              onClick={() => this.openQuickEditCoachNotes()}
            >
              <div className="only-show-mobile">
                {coachNotes.trim().length ? (
                  <Avatar
                    size={32}
                    className={`note-avatar only-show-mobile`}
                    user={coachOrganizationInformation}
                  />
                ) : null}
                <div>
                  {coachNotes.trim().length ? (
                    <span className={`coach-name`}>
                      {athleteInfo?.coach?.name}
                    </span>
                  ) : null}
                  <span className="click-add">
                    {coachNotes.trim().length
                      ? coachNotes
                      : this.props.t("calendarDay.tapToAddNote")}
                  </span>
                </div>
              </div>
              <span className="mobile-hidden">
                {coachNotes.trim().length
                  ? coachNotes
                  : this.props.t("calendarDay.clickToAddEditNotes")}
              </span>
            </div>
          ) : (
            <div className="edit-mode editing">
              <textarea
                value={this.state.coachNotes || ""}
                onChange={({ target: { value } }) =>
                  this.handleCoachNoteChange(value)
                }
                id="coach-notes-textarea"
              />
              <div className="button-container">
                <button
                  data-button-type="primary"
                  onClick={() => this.saveQuickEditCoachNotes()}
                  type="button"
                >
                  {this.props.t("calendar.save")}
                </button>
                <button
                  data-button-type="secondary"
                  onClick={() => this.closeQuickEditCoachNotes()}
                  type="button"
                >
                  <SvgIcon
                    src="/img/new-icons/red-cross.svg"
                    noFilterOnLight={true}
                  />
                </button>
              </div>
            </div>
          )}
        </div>
        {notes && notes.trim().length ? (
          <div className="athlete-notes">
            <label>
              {activeUserToRequest.referAsClients
                ? this.props.t("calendarDay.clientNotes")
                : this.props.t("calendarDay.athleteNotes")}
            </label>
            <div className="edit-mode cursor-default!">
              <span className={`athlete-name only-show-mobile`}>
                {athleteInfo?.name}
              </span>
              <span>{notes}</span>
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  render() {
    const { t } = this.props;
    const { locals } = this.context;
    const {
      query,
      activeExercises,
      editExercises,
      editCycle,
      cycleDetail = {},
      workoutNote,
      workoutCoachNote,
      athlete: athleteInfo,
      pathname,
      coachOrganizationInformation,
    } = locals;
    const { comesFromPR } = this.props;
    const notes = workoutNote?.note?.note ? workoutNote.note.note : "";

    const coachNotes = workoutCoachNote?.note?.note
      ? workoutCoachNote.note.note
      : "";

    const { athlete } = query;
    const activeUser = this.activeUser;
    const user = this.user;
    const nav = query.nav || "0";

    const program = find(get(editCycle, "programs", []), {
      order: cycleDetail.program,
    });
    const sessionOrderInProgram = program
      ? getSessionOrder(
          program,
          cycleDetail.programWeek,
          cycleDetail.programSession
        )
      : undefined;
    const session = find(get(program, "sessions", []), {
      order: sessionOrderInProgram,
    });

    const activeDay = moment(query.date, DATE_DAY_FORMAT);
    const { photoModal } = this.state;

    const dailyStats = locals.dailyStats.find((stats) => {
      const utcDate = parseInt(activeDay.format("YYYYMMDD"), 10);
      return stats.utcDate === utcDate;
    });

    const groupedExercises = groupExercisesByComplex(activeExercises);

    return (
      <main className="Calendar Calendar-Day">
        <TopBarSingleDay
          query={query}
          activeDay={activeDay}
          isAthlete={true}
          dateClassNames={"mobile-hidden"}
          comesFromPR={comesFromPR}
          {...{ locals }}
        />

        <div className="athlete-section-header only-show-mobile">
          <AthleteLinks
            athlete={athleteInfo}
            pathName={pathname}
            classNames={"only-show-mobile"}
            query={query}
          />
          <div>
            <div className="athlete-select dropdown-athlete">
              <div
                className="avatar"
                style={{
                  backgroundImage: `url(${getAvatarUrl(athleteInfo?._id)})`,
                }}
              />
              <AthleteSelect locals={this.context.locals} />
            </div>
            <ToggleLinksInput
              name="view"
              options={[
                {
                  text: t("calendar.month"),
                  value: "month",
                  href: `/actions/refresh?view=month&month=${
                    query.week
                      ? moment(query.week, DATE_DAY_FORMAT).format(
                          DATE_YEAR_FORMAT
                        )
                      : query.date
                        ? moment(query.date, DATE_DAY_FORMAT).format(
                            DATE_YEAR_FORMAT
                          )
                        : ""
                  }&week=&date=`,
                },
                {
                  text: t("calendar.day"),
                  value: "day",
                  href: `/actions/refresh?view=day&date=${
                    moment().isSame(query.month, "month")
                      ? moment().format(DATE_DAY_FORMAT)
                      : moment(query.month, DATE_YEAR_FORMAT).format(
                          DATE_DAY_FORMAT
                        )
                  }&month=&week=`,
                },
              ]}
            />
          </div>
        </div>

        <DatePicker {...{ locals }} />

        <section className="Calendar-Day">
          {editCycle && (
            <SingleDayCycleBar
              exerciseCycleDetails={cycleDetail}
              cycle={editCycle}
              cycleExercises={editExercises}
              exercises={activeExercises}
              activeDay={activeDay}
              setSessionOrder={this.setSessionOrder}
              nav={nav}
              isAthlete={true}
              pathname={pathname}
            />
          )}
          <section className="Daily-Workout">
            <DaylyWorkoutHeader
              activeExercises={activeExercises}
              sessionOrderInProgram={sessionOrderInProgram}
              query={query}
              openQuickEditMode={this.openQuickEditMode}
              handleRemove={(e) => handleRemove(e, activeDay, athlete, t)}
              onAddWorkoutNote={
                !!activeExercises &&
                !pickWorkoutNote(locals.activeSections || []) &&
                !this.state.creatingWorkoutNote
                  ? () => this.setState({ creatingWorkoutNote: true })
                  : undefined
              }
              onAddSection={
                activeExercises?.length
                  ? () =>
                      submit(
                        `/actions/exercise-section/create?proxyAthlete=${athlete}&reqSource=calendar`,
                        {
                          method: "POST",
                          body: {
                            name: t("exerciseEditor.newSection"),
                            // Below all content, like week edit mode, so
                            // exercises added afterwards fall under it.
                            order:
                              getHighestOrder(
                                activeExercises,
                                locals.activeSections
                              ) + 0.5,
                            utcDate: Number(activeDay.format("YYYYMMDD")),
                            date: activeDay.toJSON(),
                          },
                        }
                      )
                  : undefined
              }
              dailyStats={dailyStats}
              activeUser={user}
              activeDay={activeDay}
            />

            <ExerciseFrame
              activeExercises={groupedExercises}
              ungroupedExercises={activeExercises}
              sections={locals.activeSections || []}
              creatingWorkoutNote={this.state.creatingWorkoutNote}
              workoutNoteCreateBody={{
                utcDate: Number(activeDay.format("YYYYMMDD")),
                date: activeDay.toJSON(),
              }}
              onCancelCreateWorkoutNote={() =>
                this.setState({ creatingWorkoutNote: false })
              }
              openQuickEdit={this.state.openQuickEdit}
              editCycle={editCycle}
              program={program}
              session={session}
              fromMetric={locals.fromMetric}
              activeUser={activeUser}
              handleDuplicate={(exercise) =>
                this.handleDuplicateExercise(exercise, athlete)
              }
              openQuickEditMode={this.openQuickEditMode}
              handleQuickEditChange={this.handleQuickEditChange}
              handleAddSetLocally={this.handleAddSetLocally}
              loadInputs={this.loadInputs}
              intensityInputs={this.intensityInputs}
              query={query}
              saveQuickEdit={(e) => this.saveQuickEdit(e, athlete)}
              exercises={this.state.exercises}
              closeQuickEditMode={this.closeQuickEditMode}
              handleRemove={(e) => handleRemove(e, activeDay, athlete, t)}
            />

            <footer>
              <div>
                <VideoRecordComponent
                  date={activeDay.format(DATE_DAY_FORMAT)}
                  unformattedDate={activeDay}
                  videoRecordEmbed={
                    locals.videoRecord ? locals.videoRecord.embedUrl : null
                  }
                  locals={locals}
                />
                {this.renderBodyPhotos(activeDay)}
              </div>
              <div className="separator-line only-show-mobile" />
              {this.renderNotes({
                notes,
                coachNotes,
                athleteInfo,
                activeUserToRequest: activeUser,
                coachOrganizationInformation,
              })}
            </footer>
          </section>
          <hr />
          <QuickMetrics locals={locals} />
        </section>

        {photoModal.open ? (
          <PhotoModal toggleModal={this.toggleModal} photo={photoModal} />
        ) : null}
        <InsertUndoToast athleteId={athlete || get(athleteInfo, "_id")} />
      </main>
    );
  }
}

function handleRemove(e, date, athlete, t) {
  if (confirm(t("calendarDay.removeWorkoutConfirm"))) {
    e.preventDefault();

    submit(
      `/actions/exercise/unschedule-workout?date=[${date.format(
        DATE_DAY_FORMAT
      )}]&proxyAthlete=${athlete}`,
      {
        method: "POST",
        body: {
          days: [date.format(DATE_DAY_FORMAT)],
          source: "calendarDayView",
        },
      }
    );
  }
}

View.contextType = RillContext;

export default withTranslation("athlete-portal")(View);
